import { ChevronUp } from '@raydiant/raydial/components/Icons';
import { useSelector } from 'react-redux';
import { useLibrary } from '../../../../../hooks';
import ApplicationIcon from '../../../../../pages/LibraryPage/v2/components/ApplicationIcon';
import {
  getPresentationData,
  sortContent,
  mergeLibrary,
  DEFAULT_FOLDER_DATA,
} from '../../../../../pages/LibraryPage/utils';
import Spinner from '@raydiant/raydial/components/Spinner';
import Error from '../../../../../components/Error/Error';
import { memo, useMemo } from 'react';
import { selectSideSortOptions } from '../../../selectors';
import { DEFAULT_QUERY } from '../../../../AddContentPage/AddContentPageV2';
import LibraryTreeV2Item from '../../../../../components/LibraryTree/v2/LibraryTreeV2Item';
import Button from '@raydiant/raydial/components/Button';
import { useLocation, useRouteMatch } from 'react-router-dom';
import * as paths from '../../../../../routes/paths';

interface PlaylistSelectorProps {
  openedFolders: {
    [key: string]: boolean;
  };
  onFolderClick: (id: string) => void;
  onMove: (folderId: string) => void;
  scope: string;
  profileId: string;
  folderId: string;
  isRootFolder?: boolean;
  level?: number;
  disabledFolderIds: string[];
}

const MoveContentTree: React.FC<PlaylistSelectorProps> = ({
  openedFolders,
  onFolderClick,
  onMove,
  scope,
  profileId,
  folderId,
  isRootFolder = false,
  level = 0,
  disabledFolderIds = [],
}) => {
  const location = useLocation();

  const sortOptions = useSelector(selectSideSortOptions);

  // Router & URL
  const myLibraryMatch = useRouteMatch({
    path: paths.library.pattern,
    exact: true,
  });
  const sharedMatch = useRouteMatch({
    path: paths.librarySharedWithMe.pattern,
  });

  const queryParams = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    return {
      scope: sharedMatch ? 'shared' : 'owned',
      profile_id: searchParams.get('profile_id') ?? '',
      search: '',
      folder_id: searchParams.get('folder_id') ?? '',
      sort_by: '',
      sort_direction: '',
      back_to: searchParams.get('back_to') ?? '',
    };
  }, [location.search, sharedMatch]);

  const { data: libraryData, isLoading: libraryLoading } = useLibrary(
    {
      ...DEFAULT_QUERY,
      scope: queryParams.scope,
      profile_id: profileId || '',
      folder_id: folderId || '',
      search: '',
    },
    { enabled: isRootFolder || openedFolders[folderId] },
  );

  if (isRootFolder && libraryLoading) {
    <div className="flex justify-center items-center flex-1">
      <Spinner size="large" />
    </div>;
  }

  const mergedLibrary = useMemo(
    () => mergeLibrary(libraryData?.data || []),
    [libraryData],
  );

  const folders = useMemo(
    () => mergedLibrary.filter((content) => content.resourceType === 'folder'),
    [mergedLibrary],
  );

  const playlists = useMemo(
    () =>
      mergedLibrary.filter((content) => content.resourceType === 'playlist'),
    [mergedLibrary],
  );

  const presentations = useMemo(
    () =>
      mergedLibrary.filter(
        (content) => content.resourceType === 'presentation',
      ),
    [mergedLibrary],
  );

  const sortedFolders = useMemo(
    () => sortContent(folders, sortOptions),
    [folders, sortOptions],
  );

  const sortedPlaylists = useMemo(
    () => sortContent(playlists, sortOptions),
    [playlists, sortOptions],
  );

  const sortedPresentations = useMemo(
    () => sortContent(presentations, sortOptions),
    [presentations, sortOptions],
  );

  const sortedLibraryData = useMemo(() => {
    return [...sortedFolders, ...sortedPlaylists, ...sortedPresentations];
  }, [sortedFolders, sortedPlaylists, sortedPresentations]);

  const isEmpty = mergedLibrary.length === 0;

  const renderEmptyState = () => {
    const currentUserLibrary = !profileId && scope === 'owned';

    if (currentUserLibrary) {
      return (
        <div className="px-2 py-4">
          <Error
            title="No results"
            message="Hm, there’s nothing to add. Please create playlist and add some of your presentations!"
          />
        </div>
      );
    }

    return <Error />;
  };

  if (isRootFolder && isEmpty) {
    return renderEmptyState();
  }

  return (
    <div>
      {isRootFolder && (
        <div
          className="flex cursor-pointer items-center h-12 px-4 gap-2 rounded-2xl hover:bg-blue-50"
          onClick={() => onFolderClick('')}
        >
          <ApplicationIcon url={DEFAULT_FOLDER_DATA.applicationIconUrl} />
          <div className="flex-1 truncate text-lg font-regular text-black p-2.5">
            My Library
          </div>
          <Button
            isDisabled={!!myLibraryMatch && queryParams.folder_id === ''}
            variant="unstyled"
            onPress={() => onMove('')}
            className="flex justify-center items-center w-24 border-2 border-gray-200 px-6 py-3 bg-white rounded-2xl h-11 text-black font-semibold shadow"
          >
            Move
          </Button>
          {openedFolders[''] ? (
            <ChevronUp style={{ transform: 'scale(1, -1)' }} />
          ) : (
            <ChevronUp />
          )}
        </div>
      )}

      {((isRootFolder && openedFolders['']) || openedFolders[folderId]) && (
        <div className="pl-2">
          {sortedLibraryData.map((content) => (
            <div key={content.resourceId} className="flex flex-col">
              {content.resourceType === 'folder' && (
                <>
                  <div
                    className="flex cursor-pointer items-center h-12 px-4 gap-2 rounded-2xl hover:bg-blue-50"
                    onClick={() => onFolderClick(content.id)}
                  >
                    <ApplicationIcon
                      url={getPresentationData(content).applicationIconUrl}
                    />
                    <div className="flex-1 truncate text-lg font-regular text-black p-2.5">
                      {content.name}
                    </div>
                    <Button
                      isDisabled={
                        queryParams.folder_id === content.id ||
                        disabledFolderIds.includes(content.id)
                      }
                      variant="unstyled"
                      onPress={() => onMove(content.id)}
                      className="flex justify-center items-center w-24 border-2 border-gray-200 px-6 py-3 bg-white rounded-2xl h-11 text-black font-semibold shadow"
                    >
                      Move
                    </Button>
                    {openedFolders[content.id] ? (
                      <ChevronUp style={{ transform: 'scale(1, -1)' }} />
                    ) : (
                      <ChevronUp />
                    )}
                  </div>

                  {openedFolders[content.id] && (
                    <div className="pl-2">
                      <MoveContentTree
                        openedFolders={openedFolders}
                        onMove={onMove}
                        onFolderClick={onFolderClick}
                        scope={scope}
                        profileId={profileId}
                        folderId={content.id}
                        isRootFolder={false}
                        level={level + 1}
                        disabledFolderIds={disabledFolderIds}
                      />
                    </div>
                  )}
                </>
              )}
              {content.resourceType !== 'folder' && (
                <LibraryTreeV2Item
                  isDisabled={true}
                  onClick={() => {}}
                  iconUrl={getPresentationData(content).applicationIconUrl}
                  label={content.name}
                  onButtonClick={() => {}}
                />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default memo(MoveContentTree);
